import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import {
  CaretLeft, CaretRight, Phone as PhoneIcon, EnvelopeSimple, VideoCamera,
  NotePencil, ListChecks, CalendarBlank, Clock
} from '@phosphor-icons/react';
import { WatchHowLink } from '../components/WatchHowLink';

const VIEWS = ['monthly', 'weekly', 'daily'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const TYPE_ICONS = { call: PhoneIcon, email: EnvelopeSimple, meeting: VideoCamera, note: NotePencil, todo: ListChecks };
const TYPE_COLORS = {
  call: 'bg-green-100 text-green-700 border-green-200',
  email: 'bg-blue-100 text-blue-700 border-blue-200',
  meeting: 'bg-purple-100 text-purple-700 border-purple-200',
  note: 'bg-amber-100 text-amber-700 border-amber-200',
  todo: 'bg-gray-100 text-gray-700 border-gray-200',
};
const PRIORITY_COLORS = { high: 'text-red-600', medium: 'text-amber-600', low: 'text-gray-500' };

export default function CalendarPage() {
  const navigate = useNavigate();
  const [view, setView] = useState('monthly');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/calendar', { params: { year, month: month + 1 } });
      setActivities(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const goMonth = (delta) => {
    setCurrentDate(new Date(year, month + delta, 1));
    setSelectedDay(null);
  };

  // Build monthly grid
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const activityMap = {};
  activities.forEach(a => {
    const d = a.scheduled_date?.substring(0, 10);
    if (d) {
      if (!activityMap[d]) activityMap[d] = [];
      activityMap[d].push(a);
    }
  });

  const selectedDateStr = selectedDay ? `${year}-${String(month + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}` : null;
  const selectedActivities = selectedDateStr ? (activityMap[selectedDateStr] || []) : [];

  // Weekly view helpers
  const getWeekStart = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - d.getDay());
    return d;
  };

  const weekDays = [];
  if (view === 'weekly') {
    const ws = getWeekStart();
    for (let i = 0; i < 7; i++) {
      const d = new Date(ws);
      d.setDate(ws.getDate() + i);
      weekDays.push(d);
    }
  }

  const goWeek = (delta) => {
    setCurrentDate(new Date(currentDate.getTime() + delta * 7 * 86400000));
  };

  const goDay = (delta) => {
    setCurrentDate(new Date(currentDate.getTime() + delta * 86400000));
  };

  const dailyDateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
  const dailyActivities = activityMap[dailyDateStr] || [];

  return (
    <div className="flex flex-col h-full" data-testid="calendar-page">
      {/* Toolbar */}
      <div className="bg-gradient-to-b from-[#E8E8E8] to-[#D0D0D0] border-b border-[#999] px-3 py-1.5 flex items-center gap-2 shrink-0">
        <CalendarBlank size={16} weight="bold" className="text-[#0B5394]" />
        <span className="text-[13px] font-bold text-[#333]">Calendar</span>
        <WatchHowLink slug="calendar" className="ml-1" />

        <div className="w-px h-5 bg-[#999] mx-2" />

        {/* Nav arrows */}
        <button onClick={() => view === 'monthly' ? goMonth(-1) : view === 'weekly' ? goWeek(-1) : goDay(-1)} className="act-toolbar-btn" data-testid="cal-prev">
          <CaretLeft size={14} weight="bold" />
        </button>
        <span className="text-[12px] font-bold text-[#333] min-w-[160px] text-center" data-testid="cal-title">
          {view === 'monthly' && `${MONTH_NAMES[month]} ${year}`}
          {view === 'weekly' && weekDays.length > 0 && `${MONTH_NAMES[weekDays[0].getMonth()]} ${weekDays[0].getDate()} - ${weekDays[6].getDate()}, ${weekDays[0].getFullYear()}`}
          {view === 'daily' && `${DAY_NAMES[currentDate.getDay()]}, ${MONTH_NAMES[month]} ${currentDate.getDate()}, ${year}`}
        </span>
        <button onClick={() => view === 'monthly' ? goMonth(1) : view === 'weekly' ? goWeek(1) : goDay(1)} className="act-toolbar-btn" data-testid="cal-next">
          <CaretRight size={14} weight="bold" />
        </button>

        <button onClick={() => setCurrentDate(new Date())} className="px-2 py-1 text-[10px] border border-[#999] bg-white hover:bg-[#E8E8E8]" data-testid="cal-today">Today</button>

        <div className="flex-1" />

        {/* View toggle */}
        <div className="flex border border-[#999] bg-white">
          {VIEWS.map(v => (
            <button key={v} onClick={() => setView(v)}
              className={`px-2.5 py-1 text-[10px] font-medium capitalize ${view === v ? 'bg-[#0B5394] text-white' : 'text-[#333] hover:bg-[#E8E8E8]'}`}
              data-testid={`cal-view-${v}`}
            >{v}</button>
          ))}
        </div>
      </div>

      {/* Calendar Content */}
      <div className="flex-1 overflow-auto p-2 bg-[#E8E8E8]">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-6 h-6 border-2 border-[#0B5394] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : view === 'monthly' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 h-full">
            {/* Monthly Grid */}
            <div className="lg:col-span-2 bg-[#F5F5F0] border border-[#999]">
              <div className="grid grid-cols-7 bg-[#E0E0E0] border-b border-[#999]">
                {DAY_NAMES.map(d => (
                  <div key={d} className="px-1 py-1.5 text-[10px] font-bold text-[#333] text-center border-r border-[#CCC] last:border-r-0">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {Array.from({ length: firstDay }).map((_, i) => (
                  <div key={`empty-${i}`} className="h-20 border-r border-b border-[#DDD] bg-[#EAEAEA]" />
                ))}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const dayActs = activityMap[dateStr] || [];
                  const isToday = dateStr === todayStr;
                  const isSelected = selectedDay === day;
                  return (
                    <div
                      key={day}
                      onClick={() => setSelectedDay(day)}
                      className={`h-20 border-r border-b border-[#DDD] p-1 cursor-pointer transition-colors overflow-hidden ${
                        isSelected ? 'bg-[#DBEAFE]' : isToday ? 'bg-[#FEF3C7]' : 'hover:bg-[#E8E8E8]'
                      }`}
                      data-testid={`cal-day-${day}`}
                    >
                      <div className={`text-[11px] font-bold mb-0.5 ${isToday ? 'text-[#0B5394]' : 'text-[#333]'}`}>{day}</div>
                      {dayActs.slice(0, 3).map(a => {
                        const Icon = TYPE_ICONS[a.activity_type] || NotePencil;
                        return (
                          <div key={a.activity_id} className={`flex items-center gap-0.5 px-1 py-0.5 mb-0.5 text-[9px] rounded-sm border ${TYPE_COLORS[a.activity_type] || TYPE_COLORS.note} ${a.completed ? 'opacity-50 line-through' : ''}`}>
                            <Icon size={8} weight="bold" />
                            <span className="truncate">{a.scheduled_time ? `${a.scheduled_time} ` : ''}{a.title}</span>
                          </div>
                        );
                      })}
                      {dayActs.length > 3 && <div className="text-[8px] text-[#999] px-1">+{dayActs.length - 3} more</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Day Detail Panel */}
            <div className="bg-[#F5F5F0] border border-[#999]">
              <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999] flex items-center justify-between">
                <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide">
                  {selectedDay ? `${MONTH_NAMES[month]} ${selectedDay}` : 'Select a Day'}
                </h3>
                {selectedDay && (
                  <button
                    onClick={() => navigate(`/tasks?date=${selectedDateStr}`)}
                    className="text-[10px] text-[#0B5394] hover:underline font-semibold"
                    data-testid="cal-add-on-day"
                    title="Add a task or activity on this day"
                  >
                    + New
                  </button>
                )}
              </div>
              {!selectedDay ? (
                <p className="text-[12px] text-[#999] text-center py-8">Click on a day to see activities</p>
              ) : selectedActivities.length === 0 ? (
                <p className="text-[12px] text-[#999] text-center py-8">No activities scheduled</p>
              ) : (
                <div className="divide-y divide-[#DDD]">
                  {selectedActivities.map(a => {
                    const Icon = TYPE_ICONS[a.activity_type] || NotePencil;
                    return (
                      <button
                        key={a.activity_id}
                        onClick={() => a.contact_id && navigate(`/contact?id=${a.contact_id}`)}
                        className="w-full text-left px-3 py-2 hover:bg-[#F0F4FB] transition-colors"
                        data-testid={`cal-activity-${a.activity_id}`}
                      >
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <Icon size={12} weight="bold" className={PRIORITY_COLORS[a.priority] || ''} />
                          <span className={`text-[12px] font-medium ${a.completed ? 'line-through text-[#999]' : 'text-[#333]'}`}>{a.title}</span>
                        </div>
                        {a.scheduled_time && <p className="text-[10px] text-[#666] flex items-center gap-1"><Clock size={9} />{a.scheduled_time}</p>}
                        <p className="text-[10px] text-[#0B5394]">{a.contact_name}</p>
                        {a.content && <p className="text-[10px] text-[#666] mt-0.5">{a.content}</p>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ) : view === 'weekly' ? (
          <div className="bg-[#F5F5F0] border border-[#999]">
            <div className="grid grid-cols-7 divide-x divide-[#CCC]">
              {weekDays.map((wd, i) => {
                const dateStr = `${wd.getFullYear()}-${String(wd.getMonth() + 1).padStart(2, '0')}-${String(wd.getDate()).padStart(2, '0')}`;
                const dayActs = activityMap[dateStr] || [];
                const isToday = dateStr === todayStr;
                return (
                  <div key={i} className="min-h-[400px]">
                    <div className={`px-2 py-1.5 border-b border-[#999] text-center ${isToday ? 'bg-[#0B5394] text-white' : 'bg-[#E0E0E0]'}`}>
                      <div className="text-[10px] font-medium">{DAY_NAMES[i]}</div>
                      <div className={`text-[14px] font-bold ${isToday ? 'text-white' : 'text-[#333]'}`}>{wd.getDate()}</div>
                    </div>
                    <div className="p-1 space-y-1">
                      {dayActs.map(a => {
                        const Icon = TYPE_ICONS[a.activity_type] || NotePencil;
                        return (
                          <div key={a.activity_id} className={`px-1.5 py-1 text-[9px] border rounded-sm ${TYPE_COLORS[a.activity_type]} ${a.completed ? 'opacity-50' : ''}`}>
                            <div className="flex items-center gap-0.5 font-medium">
                              <Icon size={8} weight="bold" />
                              {a.scheduled_time && <span>{a.scheduled_time}</span>}
                            </div>
                            <div className="truncate">{a.title}</div>
                            <div className="text-[8px] opacity-70">{a.contact_name}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* Daily View */
          <div className="bg-[#F5F5F0] border border-[#999]">
            <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999]">
              <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide">
                {DAY_NAMES[currentDate.getDay()]}, {MONTH_NAMES[month]} {currentDate.getDate()}, {year}
              </h3>
            </div>
            {dailyActivities.length === 0 ? (
              <p className="text-[12px] text-[#999] text-center py-12">No activities scheduled for this day</p>
            ) : (
              <div className="divide-y divide-[#DDD]">
                {dailyActivities.sort((a, b) => (a.scheduled_time || '').localeCompare(b.scheduled_time || '')).map(a => {
                  const Icon = TYPE_ICONS[a.activity_type] || NotePencil;
                  return (
                    <div key={a.activity_id} className="px-4 py-3 flex items-start gap-3 hover:bg-[#E8E8E8]" data-testid={`daily-activity-${a.activity_id}`}>
                      <div className="w-14 text-right shrink-0">
                        <span className="text-[12px] font-bold text-[#333]">{a.scheduled_time || '--:--'}</span>
                      </div>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border ${TYPE_COLORS[a.activity_type]}`}>
                        <Icon size={14} weight="bold" />
                      </div>
                      <div className="flex-1">
                        <p className={`text-[12px] font-medium ${a.completed ? 'line-through text-[#999]' : 'text-[#333]'}`}>{a.title}</p>
                        <p className="text-[10px] text-[#0B5394]">{a.contact_name}</p>
                        {a.content && <p className="text-[10px] text-[#666] mt-0.5">{a.content}</p>}
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-[9px] font-medium capitalize ${PRIORITY_COLORS[a.priority]}`}>{a.priority}</span>
                          {a.duration_minutes > 0 && <span className="text-[9px] text-[#999]">{a.duration_minutes} min</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="bg-[#E0E0E0] border-t border-[#999] px-3 py-1 flex items-center justify-between text-[10px] text-[#666] shrink-0">
        <span>{activities.length} scheduled activities this month</span>
        <span>{MONTH_NAMES[month]} {year}</span>
      </div>
    </div>
  );
}
